import React from 'react';
import { TypeSectionBanner } from "../../@types/sectionTypes";
import { BANNER_TYPE } from "../../../globals/constants";
import SectionInnerPageBanner from "../../sections/common/banner";
import PolicyLayout from "../../components/PolicyLayout";

const CookiePolicyPage = () => {
    const bannerData: TypeSectionBanner = {
        title: "Cookie & Tracking Policy",
        crumb: "Cookie Policy",
        image: "/assets/images/banner/1.jpg"
    };

    const sections = [
        { id: "introduction", title: "Introduction" },
        { id: "what-are-cookies", title: "What Are Cookies?" },
        { id: "essential-cookies", title: "Essential Cookies" },
        { id: "performance-cookies", title: "Performance and Analytics" },
        { id: "functional-cookies", title: "Functional Cookies" },
        { id: "marketing-cookies", title: "Marketing and Advertising" },
        { id: "tracking-technologies", title: "Other Tracking Technologies" },
        { id: "third-party-services", title: "Third-Party Services" },
        { id: "cookie-management", title: "Cookie Management" },
        { id: "contact", title: "Contact Information" }
    ];

    return (
        <div className="cookie-policy-page">
            <SectionInnerPageBanner _data={bannerData} _type={BANNER_TYPE.COMPACT} />
            
            <PolicyLayout title="Cookie & Tracking Policy" sections={sections}>

                <div id="introduction" className="section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Cookie & Tracking Policy explains how Anathoth Technologies Ltd ("we," "our," or "us") 
                        uses cookies, web beacons, and other tracking technologies on our websites and applications. 
                        This policy should be read in conjunction with our <a href="/privacy-policy" className="text-primary">Privacy Policy</a> 
                        and <a href="/terms-of-service" className="text-primary">Terms of Service</a>.
                    </p>
                </div>

                <div id="what-are-cookies" className="section">
                    <h2>2. What Are Cookies?</h2>
                    <p>
                        Cookies are small text files that are stored on your device when you visit a website. 
                        They are widely used to make websites work more efficiently and provide information to website owners.
                    </p>

                    <h3>2.1 Types of Cookies We Use</h3>
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead className="table-light">
                                <tr>
                                    <th>Cookie Type</th>
                                    <th>Purpose</th>
                                    <th>Duration</th>
                                    <th>Required</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Essential Cookies</strong></td>
                                    <td>Enable basic website functionality, security, and navigation</td>
                                    <td>Session/Persistent</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td><strong>Performance Cookies</strong></td>
                                    <td>Collect anonymous information about how visitors use our website</td>
                                    <td>2 years</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td><strong>Functional Cookies</strong></td>
                                    <td>Remember your preferences and provide enhanced features</td>
                                    <td>1 year</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td><strong>Marketing Cookies</strong></td>
                                    <td>Track visitors across websites for advertising purposes</td>
                                    <td>1 year</td>
                                    <td>No</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div id="essential-cookies" className="section">
                    <h2>3. Essential Cookies</h2>
                    <p>These cookies are necessary for the website to function and cannot be switched off. They include:</p>
                    <ul>
                        <li><strong>Session Management:</strong> Maintaining your login state and session security</li>
                        <li><strong>Security:</strong> Protecting against CSRF attacks and ensuring secure communications</li>
                        <li><strong>Load Balancing:</strong> Ensuring optimal server performance and user experience</li>
                        <li><strong>Cookie Consent:</strong> Remembering your cookie preferences</li>
                        <li><strong>Language Preferences:</strong> Storing your selected language settings</li>
                    </ul>
                </div>

                <div id="performance-cookies" className="section">
                    <h2>4. Performance and Analytics Cookies</h2>
                    <p>We use analytics cookies to understand how visitors interact with our website:</p>

                    <h3>4.1 Google Analytics</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Website traffic analysis, user behavior tracking</li>
                        <li><strong>Data Collected:</strong> Page views, session duration, bounce rate, demographics</li>
                        <li><strong>Retention:</strong> 26 months</li>
                        <li><strong>Opt Out:</strong> You can also opt out of Google Analytics on our site at any time by disabling analytics cookies in the cookie consent banner.</li>
                    </ul>

                    <h3>4.2 Hotjar</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Heatmaps, session recordings, user feedback</li>
                        <li><strong>Data Collected:</strong> Mouse movements, clicks, scrolling behavior</li>
                        <li><strong>Retention:</strong> 365 days</li>
                        <li><strong>Opt Out:</strong> You can also opt out of Hotjar on our site at any time by disabling functional cookies in the cookie consent banner.</li>
                    </ul>

                    <h3>4.3 Microsoft Clarity</h3>
                    <ul>
                        <li><strong>Purpose:</strong> User experience analytics and insights</li>
                        <li><strong>Data Collected:</strong> Session replays, heatmaps, user interactions</li>
                        <li><strong>Retention:</strong> 90 days</li>
                        <li><strong>Privacy:</strong> Personal data is masked and anonymized</li>
                    </ul>
                </div>

                <div id="functional-cookies" className="section">
                    <h2>5. Functional Cookies</h2>
                    <p>These cookies enhance your user experience by remembering your preferences:</p>
                    <ul>
                        <li><strong>Theme Preferences:</strong> Dark/light mode settings</li>
                        <li><strong>Display Settings:</strong> Font size, layout preferences</li>
                        <li><strong>Form Data:</strong> Saving progress on multi-step forms</li>
                        <li><strong>Personalization:</strong> Customized content based on your interests</li>
                        <li><strong>Accessibility:</strong> Settings for users with disabilities</li>
                    </ul>
                </div>

                <div id="marketing-cookies" className="section">
                    <h2>6. Marketing and Advertising Cookies</h2>
                    <p>With your consent, we may use marketing cookies for:</p>

                    <h3>6.1 Social Media Integration</h3>
                    <ul>
                        <li><strong>LinkedIn:</strong> Professional networking integration and analytics</li>
                        <li><strong>Facebook:</strong> Social sharing and custom audience creation</li>
                        <li><strong>Twitter:</strong> Content sharing and engagement tracking</li>
                    </ul>

                    <h3>6.2 Advertising Partners</h3>
                    <ul>
                        <li><strong>Google Ads:</strong> Remarketing and conversion tracking</li>
                        <li><strong>Facebook Pixel:</strong> Custom audiences and conversion optimization</li>
                        <li><strong>LinkedIn Campaign Manager:</strong> B2B targeted advertising</li>
                    </ul>
                </div>

                <div id="tracking-technologies" className="section">
                    <h2>7. Other Tracking Technologies</h2>
                    <h3>7.1 Web Beacons</h3>
                    <p>Small transparent images that help us understand email engagement and website usage patterns.</p>

                    <h3>7.2 Local Storage</h3>
                    <p>Browser storage for temporary data, user preferences, and application state management.</p>

                    <h3>7.3 Server Logs</h3>
                    <p>Automatic collection of technical information including:</p>
                    <ul>
                        <li>IP addresses and geographic location</li>
                        <li>Browser type and version</li>
                        <li>Operating system information</li>
                        <li>Referral URLs and search terms</li>
                        <li>Access times and duration</li>
                    </ul>
                </div>

                <div id="third-party-services" className="section">
                    <h2>8. Third-Party Services</h2>
                    <p>We integrate with various third-party services that may set their own cookies:</p>

                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead className="table-light">
                                <tr>
                                    <th>Service</th>
                                    <th>Purpose</th>
                                    <th>Privacy Policy</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Google Analytics</td>
                                    <td>Website analytics</td>
                                    <td><a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-primary">Google Privacy Policy</a></td>
                                </tr>
                                <tr>
                                    <td>Hotjar</td>
                                    <td>User experience analytics</td>
                                    <td><a href="https://www.hotjar.com/legal/policies/privacy/" target="_blank" rel="noreferrer" className="text-primary">Hotjar Privacy Policy</a></td>
                                </tr>
                                <tr>
                                    <td>Microsoft Clarity</td>
                                    <td>User behavior analytics</td>
                                    <td><a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noreferrer" className="text-primary">Microsoft Privacy Policy</a></td>
                                </tr>
                                <tr>
                                    <td>Stripe</td>
                                    <td>Payment processing</td>
                                    <td><a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="text-primary">Stripe Privacy Policy</a></td>
                                </tr>
                                <tr>
                                    <td>Intercom</td>
                                    <td>Customer support chat</td>
                                    <td><a href="https://www.intercom.com/legal/privacy" target="_blank" rel="noreferrer" className="text-primary">Intercom Privacy Policy</a></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div id="cookie-management" className="section">
                    <h2>9. Cookie Management</h2>
                    <h3>9.1 Your Choices</h3>
                    <p>You have several options for managing cookies:</p>
                    <ul>
                        <li><strong>Cookie Consent Banner:</strong> Use our cookie preference center to opt-in or opt-out of non-essential cookies</li>
                        <li><strong>Browser Settings:</strong> Configure your browser to block or delete cookies</li>
                        <li><strong>Opt-out Tools:</strong> Use industry-provided opt-out mechanisms</li>
                        <li><strong>Do Not Track:</strong> Enable Do Not Track signals in your browser</li>
                    </ul>

                    <h3>9.2 Browser-Specific Instructions</h3>
                    <h4>Google Chrome</h4>
                    <ol>
                        <li>Click the three dots menu → Settings</li>
                        <li>Click "Privacy and security" → "Cookies and other site data"</li>
                        <li>Choose your preferred cookie settings</li>
                    </ol>

                    <h4>Mozilla Firefox</h4>
                    <ol>
                        <li>Click the menu button → Options/Preferences</li>
                        <li>Select "Privacy & Security"</li>
                        <li>Under "Cookies and Site Data," choose your settings</li>
                    </ol>

                    <h4>Safari</h4>
                    <ol>
                        <li>Safari menu → Preferences</li>
                        <li>Click "Privacy" tab</li>
                        <li>Choose your cookie preferences</li>
                    </ol>

                    <h3>9.3 Impact of Disabling Cookies</h3>
                    <p>Disabling certain cookies may affect website functionality:</p>
                    <ul>
                        <li>Login sessions may not persist</li>
                        <li>Personalized content may not display correctly</li>
                        <li>Some features may be unavailable</li>
                        <li>Analytics data collection will be limited</li>
                    </ul>

                    <h3>9.4 Mobile App Tracking</h3>
                    <p>For mobile applications, you can control tracking through:</p>
                    <ul>
                        <li><strong>iOS:</strong> Settings → Privacy & Security → Tracking</li>
                        <li><strong>Android:</strong> Settings → Privacy → Ads</li>
                        <li><strong>App-specific settings:</strong> Check individual app privacy settings</li>
                    </ul>
                </div>

                <div id="contact" className="section">
                    <h2>10. Contact Information</h2>
                    <p>If you have questions about our Cookie Policy or need assistance with cookie settings, feel free to contact us or drop us an email.</p>
                    <p><strong>Contact Information:</strong></p>
                    <ul>
                        <li><strong>Email:</strong> info@theanathoth.com</li>
                        <li><strong>Phone:</strong> +256 744 666608</li>
                        <li><strong>Address:</strong> 1 Kololo Hill Drive, Kampala, Uganda</li>
                    </ul>
                    <p><em>Last updated on 01 January, 2025</em></p>
                </div>
            </PolicyLayout>
        </div>
    );
};

export default CookiePolicyPage; 